import {BehaviorSubject, combineLatest, firstValueFrom, Observable} from 'rxjs';
import {ElementRef, EventEmitter} from '@angular/core';
import {debounceTime, filter, map, take} from 'rxjs/operators';
import {ListType} from '../types/list/list-type';
import {PaginatedListDataSettings} from '../models/paginated-list-data.model';
import {ListDataService} from '../services/list-data/list-data.service';
import {ListService} from '../services/list/list.service';
import {isObject} from './is-object.util';

export class PaginatedList<T> {
    public markedItems$: BehaviorSubject<Array<string>>;
    public deletedItems$: BehaviorSubject<Array<string>>;
    public inaccessibleDocumentIds: BehaviorSubject<Array<string>>;
    public isInitialized$: BehaviorSubject<boolean>;
    public offset: number;
    public element: ElementRef | undefined;
    public readonly listType: ListType;
    public customToastMessage$: BehaviorSubject<string | undefined>;
    public readonly dataList$: BehaviorSubject<Array<T | number>>;
    public readonly numberData$: Observable<number>;
    public listResetEvent: EventEmitter<void>;
    public listReloadEvent: EventEmitter<void>;
    public listFetchedEvent: EventEmitter<Array<unknown>>;
    public listStartedEvent: EventEmitter<void>;
    public hasMarkedOrDeletedItems$: Observable<boolean>;
    public activated: Date | undefined;
    protected isLoading$: BehaviorSubject<boolean>;
    protected isInitializing$: BehaviorSubject<boolean>;
    private name: string;
    private idKey: string;
    private readonly limit: number;
    private readonly fetchedPages: Set<number>;
    private readonly initFunction$: BehaviorSubject<((useCachedResult: boolean) => Promise<number>) | undefined>;
    private preFilterFunction: ((data: Array<T>, idKey: string) => Promise<Array<T>>) | undefined;
    private readonly fetchFunction$: BehaviorSubject<((offset: number, limit: number, useCachedResult: boolean) => Promise<Array<unknown> | boolean>) | undefined>;
    private readonly showRefreshListToast$: BehaviorSubject<boolean>;
    private isCaching: boolean;
    private readonly listDataService: ListDataService | undefined;
    private readonly listService: ListService | undefined;
    private amountOfData: number;
    private alreadyInList: boolean;
    private needToLoadIndex: Array<number>;
    private debounceLoadOffsetData: number | undefined;
    private useApiCache: boolean;

    public constructor(listType: ListType, limitData: number = 40, idKey: string = 'id', listDataService: ListDataService | undefined = undefined, listService: ListService | undefined = undefined) {
        this.name = this.listType = listType;
        this.limit = limitData;
        this.idKey = idKey;
        this.listDataService = listDataService;
        this.listService = listService;
        this.activated = undefined;
        this.needToLoadIndex = [];
        this.alreadyInList = false;
        this.amountOfData = 0;
        this.offset = 0;
        this.isCaching = false;
        this.dataList$ = new BehaviorSubject<Array<T | number>>([]);
        this.numberData$ = this.dataList$.pipe(map(list => list.length));
        this.fetchedPages = new Set<number>();
        this.isInitializing$ = new BehaviorSubject<boolean>(false);
        this.isInitialized$ = new BehaviorSubject<boolean>(false);
        this.isLoading$ = new BehaviorSubject<boolean>(false);
        this.listStartedEvent = new EventEmitter<void>();
        this.listFetchedEvent = new EventEmitter<Array<unknown>>();
        this.listResetEvent = new EventEmitter<void>();
        this.listReloadEvent = new EventEmitter<void>();
        this.initFunction$ = new BehaviorSubject<((useCachedResult: boolean) => Promise<number>) | undefined>(undefined);
        this.fetchFunction$ = new BehaviorSubject<((offset: number, limit: number, useCache: boolean) => Promise<Array<unknown> | boolean>) | undefined>(undefined);
        this.markedItems$ = new BehaviorSubject<Array<string>>([]);
        this.deletedItems$ = new BehaviorSubject<Array<string>>([]);
        this.inaccessibleDocumentIds = new BehaviorSubject<Array<string>>([]);
        this.showRefreshListToast$ = new BehaviorSubject<boolean>(false);
        this.customToastMessage$ = new BehaviorSubject<string | undefined>(undefined);
        this.hasMarkedOrDeletedItems$ = combineLatest([
            this.markedItems$.pipe(map(markedItems => markedItems.length > 0)),
            this.deletedItems$.pipe(map(deletedItems => deletedItems.length > 0)),
            this.showRefreshListToast$,
        ])
            .pipe(map(([hasMarkedItems, hasDeletedItems, showRefreshListToast]: [boolean, boolean, boolean]) => {
                return hasMarkedItems || hasDeletedItems || showRefreshListToast;
            }));
        this.useApiCache = false;
    }

    public markItem(identifier: string, global: boolean = true): void {
        const markedItems = [...this.markedItems$.getValue()];
        markedItems.push(identifier);
        this.markedItems$.next(markedItems);
        if (global) {
            this.listService?.markItem(identifier)
                .then();
        }
        this.setRefreshListToast(true);
        this.saveDataToStore();
    }

    public setItemDeleted(identifier: string, global: boolean = true): void {
        const deletedItems = [...this.deletedItems$.getValue()];
        deletedItems.push(identifier);
        this.deletedItems$.next(deletedItems);
        if (global) {
            this.listService?.deleteItem(identifier)
                .then();
        }
        this.setRefreshListToast(true);
        this.saveDataToStore();
    }

    public setInitFunction(initFunction: (useCachedResult: boolean) => Promise<number>, overwrite: boolean = false): this {
        if (this.isInitialized$.getValue() && !overwrite && this.isCaching) {
            return this;
        }

        this.initFunction$.next(initFunction);

        return this;
    }

    public setFetchFunction(fetchFunction: (offset: number, limit: number, getCachedResult: boolean) => Promise<Array<T> | boolean>, overwrite: boolean = false): this {
        if (this.isInitialized$.getValue() && !overwrite && this.isCaching) {
            return this;
        }
        this.fetchFunction$.next(fetchFunction);
        return this;
    }

    public setPreFilterFunction(preFilterFunction: ((data: Array<T>, idKey: string) => Promise<Array<T>>) | undefined): this {
        this.preFilterFunction = preFilterFunction;
        return this;
    }

    public setName(name: string): void {
        this.name = name;
    }

    public getName(): string {
        return this.name;
    }

    public fetchMoreData(index: number): void {
        this.offset = Math.max(index, 0);

        this.needToLoadIndex.push(index);

        if (this.debounceLoadOffsetData) {
            window.clearTimeout(this.debounceLoadOffsetData);
        }

        this.debounceLoadOffsetData = window.setTimeout(() => {
            const indexes = this.needToLoadIndex.toSorted((firstNumberItem, secondNumberItem) => firstNumberItem - secondNumberItem);
            this.needToLoadIndex = [];
            this.fetchingData(indexes[0], indexes.length)
                .then();
        }, 100);
    }

    public async startList(overwrite: boolean = false): Promise<PaginatedList<T>> {
        if (!this.isInitialized$.getValue() || overwrite) {
            this.isInitialized$.next(false);
            this.isInitializing$.next(true);

            await firstValueFrom(combineLatest([
                this.fetchFunction$,
                this.initFunction$
            ])
                .pipe(
                    debounceTime(0),
                    filter((fetch, init) => fetch !== undefined && init !== undefined),
                    take(1)
                ));

            if (!this.alreadyInList) {
                await this.fetchAmount();
            }

            this.listStartedEvent.emit();
            this.isInitializing$.next(false);
            this.isInitialized$.next(true);
        }
        return this;
    }

    public clearList(): void {
        this.fetchedPages.clear();
        this.dataList$.next([]);
        this.setRefreshListToast(false);
        this.markedItems$.next([]);
        this.deletedItems$.next([]);
        this.inaccessibleDocumentIds.next([]);
        this.customToastMessage$.next(undefined);
        this.alreadyInList = false;
        if (this.isCaching && this.listDataService) {
            this.listDataService.removeListData(this.name);
        }
        this.saveDataToStore();
    }

    public reloadList(): void {
        this.useApiCache = false;
        this.clearList();
        this.listReloadEvent.emit();
    }

    public async resetList(): Promise<void> {
        this.alreadyInList = false;
        this.clearList();
        this.isInitialized$.next(false);
        await this.startList();
        this.listResetEvent.emit();
    }

    public setRefreshListToast(refreshListToast: boolean, saveData: boolean = false): void {
        this.showRefreshListToast$.next(refreshListToast);
        if (saveData) {
            this.saveDataToStore();
        }
    }

    public setData(amount: number, data: Array<T>, markedItems: Array<string>, deletedItems: Array<string>, settings: PaginatedListDataSettings, showRefreshListToast: boolean): void {
        this.alreadyInList = true;
        this.amountOfData = amount;
        this.idKey = settings.idKey;
        this.setRefreshListToast(showRefreshListToast);
        this.markedItems$.next(markedItems);
        this.deletedItems$.next(deletedItems);
        let offset = -1;
        for (const [index, item] of data.entries()) {
            if (!isObject(item)) {
                if (offset === -1) {
                    offset = index;
                }
            }
        }
        if (offset === -1) {
            this.offset = data.length;
        }
        this.dataList$.next(data);
    }

    public async fetchAmount(): Promise<void> {
        const initFunction = this.initFunction$.getValue();

        if (initFunction) {
            const amountOfData = await initFunction(this.useApiCache);
            this.amountOfData = amountOfData;
            this.dataList$.next(Array.from({ length: amountOfData })
                .map((a, index) => {
                    return index;
                }));

            if (this.isCaching && this.listDataService) {
                this.initStoreData();
            }
        }
    }

    public setCaching(cache: boolean): void {
        this.isCaching = cache;
    }

    public getIdKey(): string {
        return this.idKey;
    }

    public async getIds(): Promise<Array<string>> {
        return await firstValueFrom(this.dataList$.pipe(map(items => {
            items = items.filter(item => typeof (item) !== 'number');
            return items.map(item => ((item as T)[this.idKey as keyof T]) as unknown as string);
        })));
    }

    private saveDataToStore(): void {
        if (this.isCaching && this.listDataService) {
            this.listDataService?.updateListData(this.name, this.dataList$.getValue(), this.markedItems$.getValue(), this.deletedItems$.getValue(), this.showRefreshListToast$.getValue());
        }
    }

    private initStoreData(): void {
        if (this.isCaching && this.listDataService) {
            this.listDataService.addListData({
                id: this.name,
                amount: this.amountOfData,
                data: this.dataList$.getValue(),
                activated: new Date(),
                created: new Date(),
                updated: new Date(),
                cache: this.isCaching,
                showRefreshListToast: this.showRefreshListToast$.getValue(),
                settings: {
                    idKey: this.idKey,
                },
                marked: [],
                deleted: [],
            });
            this.alreadyInList = true;
        }
    }

    private offsetToPage(offset: number): number {
        return Math.floor(offset / this.limit) + 1;
    }

    private async fetchingData(offset: number, limit: number = this.limit): Promise<void> {
        if (limit > this.amountOfData) {
            limit = this.amountOfData;
        }

        this.isLoading$.next(true);
        let data: Array<T | number> = [];
        let fetchedData;
        let preChangeNumberOfElements = 0;

        const fetchFunction = this.fetchFunction$.getValue();

        if (fetchFunction) {
            fetchedData = await fetchFunction(offset, limit, this.useApiCache);

            if (fetchedData && typeof (fetchedData) === 'object') {
                data = [...fetchedData] as Array<T | number>;
                preChangeNumberOfElements = data.length;
            }
        }

        if (this.preFilterFunction) {
            data = await this.preFilterFunction(data as Array<T>, this.idKey);
        }

        if (preChangeNumberOfElements === 0) {
            const page = this.offsetToPage(offset);
            this.fetchedPages.delete(page);

            data = Array.from({
                    length: await this.getNumberOfDataElements(offset, limit)
                })
                .map((a, index) => {
                    return offset + index;
                });
        }

        const dataArray = structuredClone(this.dataList$.getValue());
        dataArray.splice(offset, data.length, ...data);

        this.dataList$.next(dataArray);
        this.saveDataToStore();
        this.listFetchedEvent.next(data);
        this.isLoading$.next(false);
    }

    private async getNumberOfDataElements(offset: number, limit: number): Promise<number> {
        let numberOfDataElements = offset + limit;
        const numberData = await firstValueFrom(this.numberData$);

        if (numberOfDataElements > numberData) {
            numberOfDataElements = numberData;
        }

        numberOfDataElements -= offset;

        return numberOfDataElements;
    }
}
